import { Button, Grid, Stack, Typography } from '@mui/material'
import { CargoType } from '@planning/app/api'
import { RailVisitCargoFormStore } from '@planning/pages/RailVisit/stores/RailVisitCargoFormStore'
import { NumberFieldWithUnit } from '@planning/pages/VesselVisit/Components/atoms/NumberFieldWithUnit'
import { SectionSubtitle } from '@planning/pages/VesselVisit/Components/atoms/SectionSubtitle'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'

type RailVisitCargoSubFormProps = {
  store: RailVisitCargoFormStore
  isCargoTypeLocked?: boolean
}

export const RailVisitCargoSubForm = observer(
  ({ store, isCargoTypeLocked = false }: RailVisitCargoSubFormProps) => {
    const { t } = useTranslate()
    const cargoTypeLabels: Partial<Record<CargoType, string>> = {
      [CargoType.Container]: t('container', 'Container'),
      [CargoType.GeneralCargo]: t('generalCargo', 'General cargo'),
    }
    const cargoTypeLabel =
      (store.cargoType.value && cargoTypeLabels[store.cargoType.value]) ||
      t('cargoType', 'Cargo type')

    const estimateSubtitle = useMemo(
      () => `${t('discharge', 'Discharge')} / ${t('load', 'Load').toLocaleLowerCase()}`,
      [t],
    )

    return (
      <Grid container spacing={1}>
        <Grid size={12}>
          <SectionSubtitle label={t('cargoType', 'Cargo type')} />
        </Grid>
        {store.cargoType.error && (
          <Grid mt={-1} size={{ xs: 12 }}>
            <Typography variant='caption' color='error'>
              {t('fieldIsRequired', 'Field is required.')}
            </Typography>
          </Grid>
        )}
        <Grid size={12}>
          {isCargoTypeLocked ? (
            <Button
              variant='contained'
              color='secondary'
              disableElevation
              disableRipple
              sx={{ pointerEvents: 'none' }}
            >
              {cargoTypeLabel}
            </Button>
          ) : (
            <Stack direction='row' spacing={1}>
              <Button
                variant='contained'
                color={store.cargoType.value === CargoType.Container ? 'secondary' : 'inherit'}
                onClick={() => store.patchData({ cargoType: CargoType.Container })}
              >
                {t('container', 'Container')}
              </Button>
              <Button
                variant='contained'
                color={store.cargoType.value === CargoType.GeneralCargo ? 'secondary' : 'inherit'}
                onClick={() => store.patchData({ cargoType: CargoType.GeneralCargo })}
              >
                {t('generalCargo', 'General cargo')}
              </Button>
            </Stack>
          )}
        </Grid>
        <Grid size={12}>
          <SectionSubtitle label={estimateSubtitle} />
        </Grid>
        <Grid container size={12}>
          <Grid size={6}>
            <NumberFieldWithUnit
              id='dischargeEstimate'
              name='dischargeEstimate'
              label={t('discharge', 'Discharge')}
              unit={t('cnt', 'Cnt')}
              fieldStoreProps={store.dischargeEstimate.props}
              restrictionMode='nonNegative'
            />
          </Grid>
          <Grid size={6}>
            <NumberFieldWithUnit
              name='loadEstimate'
              label={`${t('load', 'Load')}`}
              unit={t('cnt', 'Cnt')}
              fieldStoreProps={store.loadEstimate.props}
              restrictionMode='nonNegative'
            />
          </Grid>
        </Grid>
      </Grid>
    )
  },
)
